import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { TABLE_PERIOD_PRESETS, usePeriodRange } from './PeriodFilter';

function periodLabel(period, range) {
  if (period === 'custom') return range?.label || 'Custom';
  return TABLE_PERIOD_PRESETS.find(([id]) => id === period)?.[1] || 'Monthly';
}

function EmployeeChecklist({ employees, selectedIds, setSelectedIds, onBack, onDone }) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const list = employees.filter(e =>
    !q || (e.name || '').toLowerCase().includes(q) || (e.role || '').toLowerCase().includes(q),
  );
  const allSelected = list.length > 0 && list.every(e => selectedIds.includes(e.id));

  const toggle = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !list.some(e => e.id === id)));
    } else {
      const add = list.map(e => e.id).filter(id => !selectedIds.includes(id));
      setSelectedIds(prev => [...prev, ...add]);
    }
  };

  return (
    <div className="table-filter-emp-panel">
      <div className="table-filter-emp-head">
        <button type="button" className="table-filter-back" onClick={onBack} aria-label="Back">
          <Icon name="arrowLeft" size={14} />
        </button>
        <div className="table-filter-popover-title">Employees</div>
        <button type="button" className="btn btn-sm" onClick={() => setSelectedIds([])}>Clear</button>
      </div>
      <input
        className="f-input table-filter-emp-search"
        placeholder="Search employee…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <div className="table-filter-emp-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={allSelected && list.length > 0} onChange={toggleAll} aria-label="Select all" />
              </th>
              <th>Employee</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {list.map(emp => (
              <tr key={emp.id} className={selectedIds.includes(emp.id) ? 'table-filter-emp-row-on' : ''} onClick={() => toggle(emp.id)}>
                <td onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.includes(emp.id)} onChange={() => toggle(emp.id)} />
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="av av-sm" style={{ background: emp.color }}>{emp.avatar}</div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{emp.name}</span>
                  </div>
                </td>
                <td><span className="tag">{emp.role}</span></td>
              </tr>
            ))}
            {!list.length && (
              <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--muted)', padding: 16 }}>No employees match</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="table-filter-popover-foot">
        <span className="f-hint">{selectedIds.length ? `${selectedIds.length} selected` : 'All employees'}</span>
        <button type="button" className="btn btn-sm btn-primary" onClick={onDone}>Apply</button>
      </div>
    </div>
  );
}

function TableFilterMenu({ periodState, showEmployees = false, employees = [], selectedEmpIds = [], setSelectedEmpIds }) {
  const [open, setOpen] = useState(false);
  const [empPanel, setEmpPanel] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const { period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo } = periodState;
  const rangeInfo = usePeriodRange(period, customFrom, customTo);

  const isActive = period !== 'month' || customFrom || customTo || (showEmployees && selectedEmpIds.length > 0);

  const close = () => { setOpen(false); setEmpPanel(false); };

  const placePanel = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelH = panelRef.current?.offsetHeight || 320;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < Math.min(panelH, 280) && spaceAbove > spaceBelow;
    const top = openUp ? rect.top - gap : rect.bottom + gap;
    setAnchor({
      top,
      right: Math.max(12, window.innerWidth - rect.right),
      openUp,
      maxH: openUp
        ? Math.min(spaceAbove, window.innerHeight - 24)
        : Math.min(spaceBelow, window.innerHeight - 24),
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    placePanel();
    const onReflow = () => placePanel();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, empPanel, period, customFrom, customTo]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const popover = open && createPortal(
    <>
      <div className="table-filter-backdrop" onClick={close} aria-hidden="true" />
      <div
        ref={panelRef}
        className={`table-filter-popover table-filter-popover-portal${anchor?.openUp ? ' table-filter-popover-up' : ''}`}
        style={anchor ? {
          top: anchor.top,
          right: anchor.right,
          maxHeight: Math.max(anchor.maxH, 180),
          transform: anchor.openUp ? 'translateY(-100%)' : undefined,
        } : { visibility: 'hidden' }}
        role="dialog"
        aria-label="Table filters"
      >
        {!empPanel ? (
          <>
            <div className="table-filter-popover-head">
              <Icon name="filter" size={15} />
              <span>Filter</span>
            </div>
            <div className="table-filter-popover-body">
              <div className="table-filter-popover-title">Period</div>
              <div className="table-filter-options">
                {TABLE_PERIOD_PRESETS.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`table-filter-option${period === id ? ' on' : ''}`}
                    onClick={() => setPeriod(id)}
                  >
                    {label}
                    {period === id && <Icon name="check" size={14} />}
                  </button>
                ))}
              </div>
              {period === 'custom' && (
                <div className="table-filter-custom">
                  <label className="table-filter-date-lbl">
                    From
                    <input className="f-input" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
                  </label>
                  <label className="table-filter-date-lbl">
                    To
                    <input className="f-input" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
                  </label>
                </div>
              )}
              {showEmployees && (
                <button type="button" className="table-filter-option table-filter-emp-entry" onClick={() => setEmpPanel(true)}>
                  <span>Employees</span>
                  <span className="table-filter-emp-meta">
                    {selectedEmpIds.length ? `${selectedEmpIds.length} selected` : 'All'}
                    <Icon name="arrowRight" size={14} />
                  </span>
                </button>
              )}
            </div>
            <div className="table-filter-popover-foot">
              <span className="f-hint">{periodLabel(period, rangeInfo)}</span>
              <button type="button" className="btn btn-sm btn-primary" onClick={close}>Done</button>
            </div>
          </>
        ) : (
          <EmployeeChecklist
            employees={employees}
            selectedIds={selectedEmpIds}
            setSelectedIds={setSelectedEmpIds}
            onBack={() => setEmpPanel(false)}
            onDone={close}
          />
        )}
      </div>
    </>,
    document.body,
  );

  return (
    <div className="table-filter-menu">
      <button
        ref={btnRef}
        type="button"
        className={`btn btn-sm table-filter-btn${isActive ? ' table-filter-btn-on' : ''}`}
        title={isActive ? `Filtered: ${periodLabel(period, rangeInfo)}${selectedEmpIds.length ? ` · ${selectedEmpIds.length} employee(s)` : ''}` : 'Filter'}
        aria-expanded={open}
        onClick={() => { setOpen(v => !v); setEmpPanel(false); }}
      >
        <Icon name="filter" size={14} />
        {isActive && <span className="table-filter-dot" />}
      </button>
      {popover}
    </div>
  );
}

export { TableFilterMenu };
